import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/si";
import "dayjs/locale/ta";
import "dayjs/locale/en";
import relativeTime from "dayjs/plugin/relativeTime";
import i18n from "../i18n";
import { TRANSLATION_CONST } from "../_redux/_constants";
import { common } from "@mui/material/colors";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

// Set locale globally
dayjs.locale(i18n.language);

const supportedLocales = TRANSLATION_CONST
  ? Object.values(TRANSLATION_CONST).map((lang) => lang.lan)
  : ["en"];

// Listen to language changes
i18n.on("languageChanged", (lng) => {
  const locale = supportedLocales.includes(lng)
    ? lng
    : TRANSLATION_CONST?.ENGLISH?.lan ?? "en";
  dayjs.locale(locale);
});

dayjs.extend(customParseFormat);

export const DateFormatter = (
  dateString,
  currentFormat = "YYYYMMDD",
  convertedFormat = "MMM DD, YYYY",
) => {
  if (typeof dateString !== "string") return "";

  const date = dayjs(dateString, currentFormat);
  if (!date.isValid()) return "";

  const monthKey = date.format("MM");
  const day = date.format("DD");
  const year = date.format("YYYY");

  const fallback = date.format(convertedFormat); // default output
  const isSinhala = i18n.language === TRANSLATION_CONST.SINHALA.lan;

  if (
    !isSinhala ||
    (!convertedFormat.includes("MMM") && !convertedFormat.includes("MMMM"))
  ) {
    return fallback; // use Day.js normally for non-Sinhala or non-month formats
  }

  const sinhalaMonth = i18n.exists(`months.${monthKey}`)
    ? i18n.t(`months.${monthKey}`)
    : date.format("MMM");

  // Replace "MMM" or "MMMM" in format with Sinhala month name
  const output = convertedFormat
    .replace("MMMM", sinhalaMonth)
    .replace("MMM", sinhalaMonth)
    .replace("DD", day)
    .replace("YYYY", year)
    .replace("YY", year.slice(-2)); // optional: support YY

  return output;
};

export const TimeFormatter = (dateString, inputFormat = null) => {
  if (typeof dateString !== "string") {
    return "";
  }

  let date;
  if (inputFormat) {
    date = dayjs(dateString, inputFormat);
  } else {
    const formats = [
      "YYYY-MM-DD hh:mm A",
      "YYYYMMDD hh:mm A",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
    ];
    date = dayjs(dateString, formats, true);
  }

  if (!date.isValid()) {
    return "";
  }

  return date.format("hh:mm A");
};

export const DateFormatterWithTime = (dateString) => {
  if (typeof dateString !== "string") return "";

  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const monthKey = date.format("MM");
  const day = date.format("DD");
  const year = date.format("YYYY");
  const time = date.format("HH:mm A");

  const isSinhala = i18n.language === TRANSLATION_CONST.SINHALA.lan;

  if (!isSinhala) {
    return date.format("MMM DD, YYYY HH:mm A");
  }

  const sinhalaMonth = i18n.exists(`months.${monthKey}`)
    ? i18n.t(`months.${monthKey}`)
    : date.format("MMM");

  return `${sinhalaMonth} ${day}, ${year} ${time}`;
};

export const DateFormatterWithTimeWithoutLanguage = (
  dateString,
  convertedFormat = "MMM DD, YYYY hh:mm A",
) => {
  if (typeof dateString !== "string") return "";

  const date = dayjs(dateString).locale(TRANSLATION_CONST.ENGLISH.lan);
  if (!date.isValid()) return "";

  return date.format(convertedFormat);
};

export const formatPdfNameWithTime = (transactionDateTime) => {
  if (!transactionDateTime) return "LOLC_Payment_Receipt";

  const date = dayjs(transactionDateTime);

  return `LOLC_Payment_Receipt_${date.format("DD_MM_YYYY_hh_mm_ss_A")}`;
};

export function isValidExpiryDate(dateString) {
  const value = dateString.replace(/\D/g, "");

  const isValid =
    /^\d{4}$/.test(value) &&
    (() => {
      const month = parseInt(value.slice(0, 2), 10);
      const year = parseInt("20" + value.slice(2), 10);

      if (month < 1 || month > 12) return false;

      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();

      return (
        year > currentYear || (year === currentYear && month >= currentMonth)
      );
    })();

  return {
    isValid,
    message: isValid ? "" : i18n.t("Please enter a valid date."),
  };
}

export function historyFiterDateFormat(dateString = "") {
  if (!dateString) {
    return dateString;
  }

  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = date.getFullYear();

  const formattedDate = `${day}.${month}.${year}`;

  return formattedDate;
}

export const DateFormatterWithTimeOrAgo = (dateString) => {
  if (typeof dateString !== "string") return "";

  const date = dayjs(dateString);
  if (!date.isValid()) return "";

  const now = dayjs();
  const hoursDiff = now.diff(date, "hour");

  const isSinhala = i18n.language === TRANSLATION_CONST.SINHALA.lan;

  //real time is picked from the machine and check whether it is less than 24 hours, if so show the time ago instead of the time(this was a requirement)
  if (hoursDiff < 24) {
    return date.fromNow();
  }

  const monthKey = date.format("MM");
  const day = date.format("DD");
  const year = date.format("YYYY");
  const time = date.format("HH:mm A");

  if (!isSinhala) {
    return date.format("MMM DD, YYYY HH:mm A");
  }

  const sinhalaMonth = i18n.exists(`months.${monthKey}`)
    ? i18n.t(`months.${monthKey}`)
    : date.format("MMM");

  return `${sinhalaMonth} ${day}, ${year} ${time}`;
};

// Generate secure mail PDF filename: LOLC_SECUREMAIL_YYYYMMDD_HH-mm-ss.pdf
export const formatSecureMailPdfName = (prefix = "LOLC_SECUREMAIL") => {
  const date = dayjs();
  const datePart = date.format("YYYYMMDD");
  const timePart = date.format("HHmmss");
  return `${prefix}_${datePart}_${timePart}.pdf`;
};
